// ============================================================================
// v4 — Ads Manager, rebuilt from scratch as a modern attribution dashboard.
// Wired directly to the demo dataset (bypasses the legacy table/transform).
// ============================================================================
import React, { useState } from 'react';
import { getDataset, sumRange, seriesRange } from 'app/demo/dataset';
import { Spark, AreaChart, Donut } from './viz';
import { ChannelIcon } from './v4ui';
import markPhoto from 'static/images/v4/mark.png';
import './dashboard.scss';

const shortName = (n) => n.split('—').pop().trim();

// ---- formatting helpers ----
const fmt$ = (n) => '$' + Math.round(n).toLocaleString('en-US');
const fmt$2 = (n) => '$' + n.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const fmtN = (n) => Math.round(n).toLocaleString('en-US');
const fmtX = (n) => n.toFixed(2) + 'x';
const pct = (cur, prev) => (prev > 0 ? ((cur - prev) / prev) * 100 : 0);

const TIER = {
  prospecting: { label: 'Prospecting', color: '#5A54F3' },
  retargeting: { label: 'Retargeting', color: '#38bdf8' },
  retention: { label: 'Retention', color: '#8AE8BD' },
  brand: { label: 'Brand', color: '#ec4899' },
};

function rec(roas) {
  if (roas >= 4.5) return { label: 'Scale', cls: 'good' };
  if (roas >= 2.5) return { label: 'Maintain', cls: 'mid' };
  return { label: 'Review', cls: 'low' };
}

export default function DashboardV4() {
  const ds = getDataset();
  const dates = ds.dates;
  const last = dates.length - 1;
  const win = 30;
  const from = dates[Math.max(0, last - (win - 1))];
  const to = dates[last];
  const pFrom = dates[Math.max(0, last - (win * 2 - 1))];
  const pTo = dates[Math.max(0, last - win)];

  const cur = sumRange(ds.accountDaily, from, to);
  const prev = sumRange(ds.accountDaily, pFrom, pTo);
  const daily = seriesRange(ds.accountDaily, from, to); // [{day, totals}]
  const dayLabels = daily.map((d) => {
    const [, m, dd] = d.day.split('-');
    return `${parseInt(dd)}/${parseInt(m)}`;
  });

  const [tab, setTab] = useState('campaigns');

  // KPI cards
  const kpis = [
    { key: 'rev', label: 'Attributed Revenue', value: fmt$(cur.revenue), delta: pct(cur.revenue, prev.revenue), live: true, color: '#5A54F3', series: daily.map((d) => d.totals.revenue) },
    { key: 'spend', label: 'Ad Spend', value: fmt$(cur.adspend), delta: pct(cur.adspend, prev.adspend), color: '#38bdf8', series: daily.map((d) => d.totals.adspend), invert: true },
    { key: 'roas', label: 'Blended ROAS', value: fmtX(cur.roas), delta: pct(cur.roas, prev.roas), color: '#8AE8BD', series: daily.map((d) => d.totals.roas) },
    { key: 'orders', label: 'Orders', value: fmtN(cur.orders), delta: pct(cur.orders, prev.orders), color: '#7A75F6', series: daily.map((d) => d.totals.orders) },
    { key: 'aov', label: 'Avg Order Value', value: fmt$2(cur.aov), delta: pct(cur.aov, prev.aov), color: '#f472b6', series: daily.map((d) => d.totals.aov) },
    { key: 'profit', label: 'Profit', value: fmt$(cur.profit), delta: pct(cur.profit, prev.profit), color: '#22d3ee', series: daily.map((d) => d.totals.profit) },
  ];

  // Revenue by channel/tier (donut)
  const tierTotals = {};
  ds.campaigns.forEach((c) => {
    const m = sumRange(c.daily, from, to);
    tierTotals[c.tier] = (tierTotals[c.tier] || 0) + m.revenue;
  });
  const donutSegs = Object.keys(tierTotals)
    .sort((a, b) => tierTotals[b] - tierTotals[a])
    .map((t) => ({ label: TIER[t].label, color: TIER[t].color, value: tierTotals[t] }));
  const totalRev = donutSegs.reduce((a, s) => a + s.value, 0);

  // Table rows for the active level
  let rows = [];
  if (tab === 'campaigns') {
    rows = ds.campaigns.map((c) => ({ id: c.id, name: c.name, tier: c.tier, daily: c.daily }));
  } else if (tab === 'adsets') {
    ds.campaigns.forEach((c) => c.adsets.forEach((a) => rows.push({ id: a.id, name: a.name, sub: c.name, tier: c.tier, daily: a.daily })));
  } else {
    ds.campaigns.forEach((c) => c.adsets.forEach((a) => a.ads.forEach((ad) => rows.push({ id: ad.id, name: ad.name, sub: a.name, tier: c.tier, daily: ad.daily }))));
  }
  rows = rows
    .map((r) => {
      const m = sumRange(r.daily, from, to);
      const spark = seriesRange(r.daily, dates[Math.max(0, last - 13)], to).map((d) => d.totals.roas);
      return { ...r, m, spark, roas: m.roas };
    })
    .sort((a, b) => b.m.revenue - a.m.revenue);

  // best & worst tier for the AI insight
  const bestTier = donutSegs[0];
  const worstCampaign = [...ds.campaigns].map((c) => ({ c, m: sumRange(c.daily, from, to) })).sort((a, b) => a.m.roas - b.m.roas)[0];
  const bestCampaign = [...ds.campaigns].map((c) => ({ c, m: sumRange(c.daily, from, to) })).sort((a, b) => b.m.roas - a.m.roas)[0];

  return (
    <div className="v4-dash">
      {/* ---- top bar ---- */}
      <header className="v4-top">
        <div>
          <h1>Ads Manager</h1>
          <p className="v4-sub">Attribution across all channels · Solène</p>
        </div>
        <div className="v4-top-right">
          <span className="v4-live"><i /> Live · updated just now</span>
          <button className="v4-pill">Last 30 days ▾</button>
          <button className="v4-export">Export</button>
        </div>
      </header>

      {/* ---- campaign table (Ads Manager is just the table; overview cards live on /overview) ---- */}
      <section className="v4-card v4-table-card">
        <div className="v4-table-head">
          <div className="v4-tabs">
            {['campaigns', 'adsets', 'ads'].map((t) => (
              <button key={t} className={'v4-tab ' + (tab === t ? 'active' : '')} onClick={() => setTab(t)}>
                {t[0].toUpperCase() + t.slice(1)}
              </button>
            ))}
          </div>
          <span className="v4-muted">{rows.length} {tab} · trailing 30 days</span>
        </div>
        <div className="v4-table">
          <div className="v4-tr v4-th">
            <span className="c-name">Campaign</span>
            <span>Spend</span><span>Revenue</span><span>Orders</span>
            <span>AOV</span><span>ROAS</span><span className="c-spark">Momentum</span>
            <span className="c-rec">Recommendation</span>
          </div>
          {rows.map((r, i) => {
            const rc = rec(r.roas);
            const t = TIER[r.tier];
            const highlight = tab === 'campaigns' && i === 1;
            return (
              <React.Fragment key={r.id}>
                <div className={'v4-tr' + (highlight ? ' v4-tr-hl' : '')}>
                  <span className="c-name">
                    <ChannelIcon color={t.color} />
                    <span className="nm">
                      <b>{r.name}</b>
                      {r.sub && <em>{r.sub}</em>}
                      {!r.sub && <em style={{ color: t.color }}>{t.label}</em>}
                    </span>
                  </span>
                  <span>{fmt$2(r.m.adspend)}</span>
                  <span className="pos">{fmt$2(r.m.revenue)}</span>
                  <span>{fmtN(r.m.orders)}</span>
                  <span>{fmt$2(r.m.aov)}</span>
                  <span className={'roas ' + rc.cls}>{fmtX(r.roas)}</span>
                  <span className="c-spark"><Spark data={r.spark} color={rc.cls === 'good' ? '#8AE8BD' : rc.cls === 'mid' ? '#38bdf8' : '#fbbf24'} w={92} h={26} fill={false} /></span>
                  <span className="c-rec"><b className={'pill ' + rc.cls}>{rc.label}</b></span>
                  {highlight && (
                    <div className="mark-bubble">
                      <img className="mark-bubble-av" src={markPhoto} alt="Mark" />
                      <div className="mark-bubble-body">
                        <span className="mark-bubble-name">Mark <i className="mark-bubble-live" /></span>
                        <p className="mark-bubble-txt"><b>Keep an eye on {shortName(r.name)}.</b> It's holding {fmtX(r.roas)} and quietly climbing while the rest cools off. I'm moving more budget here tomorrow.</p>
                      </div>
                    </div>
                  )}
                </div>
              </React.Fragment>
            );
          })}
        </div>
      </section>
    </div>
  );
}
